import { motion } from "framer-motion";
import { Landmark, Receipt, ShieldAlert, TrendingDown, LucideIcon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Link } from "react-router-dom";

export const BentoGrid = () => {
  const { t, getLocalizedPath } = useLanguage();

  const icons: LucideIcon[] = [TrendingDown, Landmark, Receipt, ShieldAlert];

  const featureKeys = ["efficiency", "compliance", "fiscal", "sentiment"] as const;
  const features = featureKeys.map((key, i) => ({
    icon: icons[i],
    ...t.benefits.features[key],
  }));

  return (
    <section id="benefits" className="py-32 lg:py-40 bg-muted/30">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 tracking-tight">
            {t.benefits.title} <span className="text-secondary">{t.benefits.titleHighlight}</span>
          </h2>
          <p className="text-lg text-muted-foreground font-light max-w-3xl mx-auto">
            {t.benefits.subtitle}
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group"
            >
              <div className="h-full bg-card rounded-2xl p-8 border border-border hover:border-secondary/40 transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-secondary/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="w-7 h-7 text-secondary" />
                  </div>
                  <div className="text-right">
                    <span className="text-3xl lg:text-4xl font-bold text-secondary">{feature.stat}</span>
                    <p className="text-xs text-muted-foreground mt-1">{feature.statLabel}</p>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground font-light leading-relaxed">{feature.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <p className="text-muted-foreground font-light text-lg">
            <span className="font-medium text-foreground">{t.benefits.cta}</span>{" "}
            <Link to={getLocalizedPath("/contact")} className="text-primary hover:underline font-medium inline-flex items-center gap-1">
              {t.benefits.ctaLink}
            </Link>
          </p>
        </motion.div>
      </div>
    </section>
  );
};